import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, AtomBadge, StatusPill, EmptyState } from "../components/primitives";

export default function Models() {
  const [models, setModels] = useState([]);
  useEffect(() => { api.get("/models").then(r => setModels(r.data.models || [])); }, []);
  return (
    <div>
      <PageHeader
        title="Model Risk Management"
        subtitle="Atom AI series · validation · drift · governance — ACAMS six-component framework"
        sections={["AI/ML"]}
      />
      <div className="p-6">
        <Card>
          <CardHeader title="Model inventory" subtitle="Every model in operation across the platform" />
          <table className="dense w-full">
            <thead><tr>
              <th>Model</th><th>Version</th><th>Purpose</th><th>Deployment</th><th>Last validation</th><th>Next validation</th><th>P / R</th><th>Drift</th><th>Status</th>
            </tr></thead>
            <tbody>
              {models.map(m => (
                <tr key={m.id}>
                  <td><Link to={`/models/${m.id}`} data-testid={`model-row-${m.id}`} className="text-amber-600 font-medium hover:underline">{m.name}</Link></td>
                  <td className="text-[11px]">{m.version}</td>
                  <td className="text-[11px] text-ink-muted max-w-[280px] truncate">{m.purpose}</td>
                  <td className="text-[11px]">{m.deployment}</td>
                  <td className="text-[11px]">{m.last_validation}</td>
                  <td className="text-[11px]">{m.next_validation}</td>
                  <td className="text-[11px] tabular">{m.precision ? `${m.precision.toFixed(2)} / ${m.recall.toFixed(2)}` : '—'}</td>
                  <td><span className={`w-1.5 h-1.5 rounded-full inline-block ${m.drift === 'Amber' ? 'bg-risk-medium' : m.drift === '-' ? 'bg-ink-subtle' : 'bg-risk-low'}`} /></td>
                  <td><StatusPill status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="grid grid-cols-3 gap-3 mt-3">
          <Card>
            <CardHeader title="Validation calendar" subtitle="Annual validation across all Atom models" />
            <div className="p-3 text-[12px] space-y-1.5">
              {models.filter(m => m.next_validation !== "-").map(m => (
                <div key={m.id} className="flex justify-between border-b border-line/50 py-1"><span>{m.name}</span><span className="text-ink-subtle">{m.next_validation}</span></div>
              ))}
            </div>
          </Card>
          <Card>
            <CardHeader title="Drift heat-map" subtitle="Aggregate feature drift across models" />
            <div className="p-3">
              {[
                ["Atom Proton v2.4", ["Velocity","Counterparty risk","Geo","Hour-of-day","Channel","Amount p95"]],
                ["Atom Electron v1.1", ["Name length","Token count","Phonetic dist","Translit","List freshness"]],
              ].map(([n, fs]) => (
                <div key={n} className="mb-2">
                  <div className="text-[11px] font-medium mb-1">{n}</div>
                  <div className="grid grid-cols-6 gap-0.5">
                    {fs.map((f, i) => <div key={f} className="h-6 rounded-sm flex items-center justify-center text-[9px]" style={{background: i === 1 ? 'rgba(183,121,31,0.4)' : 'rgba(31,122,77,0.25)'}}>{f.slice(0,7)}</div>)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <CardHeader title="Validation reports library" />
            <div className="p-3 text-[12px] space-y-1.5">
              {[["AE-VAL-2026-Q2","Atom Electron v1.1","14 Apr 2026"],["AP-VAL-2026-Q2","Atom Proton v2.4","22 Apr 2026"],["AN-VAL-2026-Q2","Atom Neutron v2.0","28 Apr 2026"],["ANU-VAL-2026-Q2","Atom Nucleus v3.5","05 May 2026"]].map(([id, m, d]) => (
                <div key={id} className="flex justify-between border-b border-line/50 py-1">
                  <div><div className="font-medium">{id}</div><div className="text-[10px] text-ink-subtle">{m}</div></div>
                  <button className="text-amber-600 text-[11px]">Download PDF</button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
